import {
  calcDurationToNow,
  colorByStatus,
  DataGridColumn,
  getTimeInState,
  labelByStatus,
  TagsView
} from '../../../shared';
import { ClientViewListRepresentationOutput, Field } from '@coverright/data-access/types/enrollment';
import { getUserName } from '@coverright/utils';
import { ButtonBase, Chip, Link as MuiLink, Skeleton, SxProps, Typography } from '@mui/material';
import moment from 'moment';
import { formatToPhoneNumber } from '@coverright/ui/inputs';
import { TagsFormButton } from '../../../features/tags-form';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { useStatusForm, withStatusForm } from '../../../features';
import { PropsWithChildren } from 'react';
import { keycloak } from '@coverright/shared/keycloak';
import { PencilSimpleIcon } from '@phosphor-icons/react';


export const dashboardColumns: DataGridColumn<ClientViewListRepresentationOutput, Field>[] = [
  {
    title: 'Contact',
    value: client => (
      <>
        <MuiLink component={Link} variant={'body2'} to={'/client/' + client.clientId} sx={{fontWeight: 500, mb: .1}}>
          {getUserName(client)}
        </MuiLink>
        <Typography component={'p'} sx={{ color: 'text.secondary' }} noWrap variant={'caption'}>
          {client.state ? `${getTimeInState(client.state)} in ${client.state}` : 'Missing State'}
        </Typography>
        <Typography component={'p'} sx={{ color: 'text.secondary' }} noWrap variant={'caption'}>{client.email}</Typography>
      </>
    )
  },
  {
    title: 'Birthdate',
    value: client => client.birthDate ? <div>
      <Typography variant={'body2'} fontWeight={500}>{moment(client.birthDate).format('L')}</Typography>
      <Typography sx={{ color: 'text.secondary' }} variant={'caption'}>{Math.floor(moment.duration(moment().diff(moment(client.birthDate))).asYears())} yo</Typography>
    </div> : '',
    sortKey: Field.BirthDate
  },
  {
    title: 'Lead Date',
    value: client => moment(client.createdAt).format('L'),
    sortKey: Field.CreatedAt
  },
  {
    title: 'Phone',
    value: client => formatToPhoneNumber(client.phoneNumber),
    sortKey: Field.PhoneNumber
  },
  {
    title: 'Calls',
    value: client => client.callsCount
  },
  {
    title: 'Status',
    value: client => <StatusCell client={client}>
      <Typography sx={{ color: colorByStatus(client.status) }}
                  variant={'body2'} fontWeight={500}>{labelByStatus(client.status)}</Typography>
    </StatusCell>
  },
  {
    title: 'Tags',
    value: client => <TagsCell client={client} />
  },
  {
    title: 'Last Call',
    value: client => (
      client.lastCallDate ? <>
        <Typography sx={{ color: 'text.secondary' }} noWrap variant={'caption'}>
          {moment(client.lastCallDate).format('MMM D, YYYY')}
        </Typography>
        <Typography component={'p'} sx={{ color: 'text.secondary' }}  variant={'caption'}>
          {moment(client.lastCallDate).format('h:mm A')} EST
        </Typography>
      </> : ''
    ),
    sortKey: Field.LastCallDate
  },
  {
    title: 'Next Contact',
    value: client => (
      <StatusCell sx={{'& p': {color: moment(client.followUpDate).isBefore(moment().startOf('day')) ? '#DE3124' : undefined} }} client={client}>
        {client.followUpDate ? <div>
          <Typography variant={'body2'} align={'left'} fontWeight={500}>{moment(client.followUpDate).format('L')}</Typography>
          <Typography component={'p'} sx={{ color: 'text.secondary' }} align={'left'} noWrap variant={'caption'}>
            {moment(client.followUpDate).format('MMM D, YYYY')}
          </Typography>
          <Typography component={'p'} sx={{ color: 'text.secondary' }} align={'left'} variant={'caption'}>
            {moment(client.followUpDate).format('h:mm A')} EST
          </Typography>
        </div> : <PencilSimpleIcon size={16} color="#1c434f" />}
      </StatusCell>
    ),
    sortKey: Field.FollowUpDate
  },
  {
    title: 'Expected Close Date',
    value: client => (
      <StatusCell client={client}>
        {client.expectedCloseDate ? (
          <Typography variant={'body2'} fontWeight={500}>{moment(client.expectedCloseDate).format('L')}</Typography>
        ) : <PencilSimpleIcon size={16} color="#1c434f" />}
      </StatusCell>
    ),
    sortKey: Field.FollowUpDate
  }
];

const StatusCell = withStatusForm(({client, sx = {}, children}: PropsWithChildren<{ sx?: SxProps, client: ClientViewListRepresentationOutput }>) => {
  const {open} = useStatusForm();

  if (!client) {
    return <Skeleton variant={'rounded'} width={125} height={48} />
  }

  return <ButtonBase onClick={() => open({
    ...client,
    id: client.clientId,
    agent: {
      id: client.agentId,
    }
  })} sx={sx} >
    {children}
  </ButtonBase>
})

const TagsCell = ({client}: { client: ClientViewListRepresentationOutput }) => {
  return (
    <TagsFormButton clientId={client.clientId} tags={client.tags}>
      {client.tags && client.tags.length ? <TagsView tags={client.tags} /> :
        <Chip variant="outlined" label={'No tags added'} size={'small'} sx={{border: '1px dashed #b9b9b9', '& *': {color: '#b9b9b9'} }} />}
    </TagsFormButton>
  )
}
